import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { EmployeeLeaveAllowanceFragment, EmployeeLeaveUsageFragment } from '@/services/gql/graphql.generated';
import { useLeaveTypes } from '@/services/leaveManagement/LeaveTypesProvider';
import { strings } from '@/services/translation/strings';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

const columnWidths = {
  leaveType: '25%',
  carriedOver: '15%',
  allowance: '15%',
  taken: '15%',
  pending: '15%',
  remaining: '15%'
};

const pageSize = 20;

interface AnnualLeaveTableProps {
  leaveTypes: ReturnType<typeof useLeaveTypes>;
  allowances: EmployeeLeaveAllowanceFragment[];
  leaveUsage?: EmployeeLeaveUsageFragment[];
}

export const TeamMemberAnnualLeaveTable: FC<AnnualLeaveTableProps> = ({ leaveTypes, allowances, leaveUsage = [] }) => {
  const [offset, limit, controls] = usePagination(pageSize, leaveTypes.length);
  const paginatedLeaveTypes = leaveTypes.slice(offset, offset + limit);
  const allowancesByType = new Map(allowances.map(allowance => [allowance.leaveType.id, allowance]));
  const usageByType = new Map(leaveUsage.map(usage => [usage.leaveTypeId, usage]));

  const headerClass = 'px-4 pb-2 pt-4 text-center text-xs font-medium text-secondary-std sm:px-6';
  const cellClass = 'px-4 py-4 text-center text-sm text-primary-std sm:px-6';

  return (
    <div className="w-full overflow-hidden">
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <thead>
          <tr>
            <th className={twMerge(headerClass, 'text-left')} style={{ width: columnWidths.leaveType }}>
              {strings.timeshift.teamMember.annualLeave.leaveType}
            </th>
            <th className={headerClass} style={{ width: columnWidths.carriedOver }}>
              {strings.timeshift.teamMember.annualLeave.daysCarriedOver}
            </th>
            <th className={headerClass} style={{ width: columnWidths.allowance }}>
              {strings.timeshift.teamMember.annualLeave.totalAllowance}
            </th>
            <th className={headerClass} style={{ width: columnWidths.taken }}>
              {strings.timeshift.teamMember.annualLeave.daysTaken}
            </th>
            <th className={headerClass} style={{ width: columnWidths.pending }}>
              {strings.timeshift.teamMember.annualLeave.pending}
            </th>
            <th className={headerClass} style={{ width: columnWidths.remaining }}>
              {strings.timeshift.teamMember.annualLeave.daysRemaining}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-std">
          {paginatedLeaveTypes.map((leaveType, index) => {
            const allowance = allowancesByType.get(leaveType.id);
            const usage = usageByType.get(leaveType.id);
            const hasLimit = leaveType.allowanceAmount !== 0 && allowance !== undefined;
            const taken = hasLimit
              ? allowance.taken
              : usage?.taken ?? 0;
            const totalAllowance = hasLimit ? allowance.amount + allowance.carriedOver : null;
            const rowClass = index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';

            const displayName = leaveType.properties.displayName ?? leaveType.name;
            return (
              <tr className={rowClass} key={leaveType.id}>
                <td className={twMerge(cellClass, 'text-left')} style={{ width: columnWidths.leaveType }}>
                  <span>{displayName}</span>
                </td>
                <td className={cellClass} style={{ width: columnWidths.carriedOver }}>
                  {hasLimit ? allowance.carriedOver : '-'}
                </td>
                <td className={cellClass} style={{ width: columnWidths.allowance }}>
                  {totalAllowance ?? '-'}
                </td>
                <td className={cellClass} style={{ width: columnWidths.taken }}>
                  {taken}
                </td>
                <td className={cellClass} style={{ width: columnWidths.pending }}>
                  {hasLimit ? allowance.pending : usage?.pending ?? 0}
                </td>
                <td className={cellClass} style={{ width: columnWidths.remaining }}>
                  {hasLimit ? allowance.remaining : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {leaveTypes.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};
